<template>
  <div class="customer-service">
    <!-- 左侧对话列表 -->
    <div class="conv-list">
      <div class="conv-list-header">
        <span class="conv-list-title">会话列表</span>
        <el-badge v-if="totalUnread > 0" :value="totalUnread" :max="99" class="total-unread-badge"></el-badge>
      </div>
      <div class="conv-list-body" v-loading="convLoading">
        <div
          v-for="conv in conversations"
          :key="conv.id"
          class="conv-item"
          :class="{ active: currentConv && currentConv.id === conv.id }"
          @click="selectConversation(conv)"
        >
          <el-badge :value="conv.unreadCount > 0 ? conv.unreadCount : ''" :hidden="!conv.unreadCount || conv.unreadCount == 0" class="conv-avatar-badge">
            <el-avatar :size="44" :src="conv.avatar" icon="el-icon-user-solid"></el-avatar>
          </el-badge>
          <div class="conv-info">
            <div class="conv-info-top">
              <span class="conv-nickname">{{ conv.nickname || '用户' + conv.userIdOne }}</span>
              <span class="conv-time">{{ formatTime(conv.lastMessageTime) }}</span>
            </div>
            <div class="conv-last-msg">{{ conv.lastMessageContent || '暂无消息' }}</div>
          </div>
        </div>
        <div v-if="conversations.length === 0 && !convLoading" class="conv-empty">暂无会话</div>
      </div>
      <div v-if="convTotal > conversations.length" class="conv-load-more">
        <el-button size="small" type="text" @click="loadMoreConversations" :loading="convLoadingMore">加载更多</el-button>
      </div>
    </div>

    <!-- 右侧对话内容 -->
    <div class="chat-panel" v-if="currentConv">
      <!-- 顶部用户信息 -->
      <div class="chat-header">
        <el-avatar :size="36" :src="currentConv.avatar" icon="el-icon-user-solid"></el-avatar>
        <span class="chat-header-name">{{ currentConv.nickname || '用户' + currentConv.userIdOne }}</span>
        <el-tag size="mini" type="info" class="chat-header-id">ID: {{ currentConv.userIdOne }}</el-tag>
        <div class="chat-header-right">
          <el-tag size="mini" :type="wsConnected ? 'success' : 'danger'" class="ws-status-tag">
            {{ wsConnected ? '已连接' : '未连接' }}
          </el-tag>
        </div>
      </div>

      <!-- 消息区域 -->
      <div class="chat-messages" ref="messagesContainer" @scroll="onMessageScroll">
        <div v-if="msgLoadingMore" class="msg-loading-more">
          <i class="el-icon-loading"></i> 加载中...
        </div>
        <div v-if="noMoreMessages && messages.length > 0" class="no-more-msg">没有更多消息了</div>

        <div v-for="(msg, index) in messages" :key="msg.messageId || msg.id || index" class="msg-wrapper"
          :class="msg.fromUserId == 0 || msg.fromUserId === '0' ? 'msg-right' : 'msg-left'">
          <!-- 时间分割线 -->
          <div v-if="shouldShowTime(index)" class="msg-time-divider">{{ formatMsgTime(msg.sendTime || msg.createdAt) }}</div>

          <div class="msg-row">
            <el-avatar v-if="msg.fromUserId != 0 && msg.fromUserId !== '0'" :size="36" :src="currentConv.avatar" icon="el-icon-user-solid" class="msg-avatar"></el-avatar>
            <div class="msg-bubble" :class="msg.fromUserId == 0 || msg.fromUserId === '0' ? 'bubble-right' : 'bubble-left'">
              <!-- 文本消息 -->
              <span v-if="msg.messageType === 1">{{ msg.content }}</span>
              <!-- 图片消息 -->
              <el-image v-else-if="msg.messageType === 2" :src="msg.content" fit="contain" class="msg-img"
                :preview-src-list="[msg.content]"></el-image>
              <!-- 系统消息 -->
              <span v-else-if="msg.messageType === 4" class="sys-msg">{{ msg.content }}</span>
              <span v-else>{{ msg.content }}</span>
            </div>
            <el-avatar v-if="msg.fromUserId == 0 || msg.fromUserId === '0'" :size="36" icon="el-icon-service" class="msg-avatar admin-avatar"></el-avatar>
          </div>
          <!-- 右侧消息状态（仅客服发出的消息显示） -->
          <div v-if="msg.fromUserId == 0 || msg.fromUserId === '0'" class="msg-status-bar">
            <span v-if="msg.status === 3" class="msg-status msg-status-read">
              <i class="el-icon-check"></i><i class="el-icon-check msg-status-check2"></i> 已读
            </span>
            <span v-else-if="msg.status === 2" class="msg-status msg-status-delivered">
              <i class="el-icon-check"></i><i class="el-icon-check msg-status-check2"></i> 已送达
            </span>
            <span v-else class="msg-status msg-status-sent">
              <i class="el-icon-check"></i> 已发送
            </span>
          </div>
        </div>

        <div v-if="messages.length === 0 && !msgLoading" class="no-msg">暂无消息记录</div>
      </div>

      <!-- 输入区域 -->
      <div class="chat-input-area">
        <div class="chat-toolbar">
          <el-upload
            action="#"
            :http-request="uploadImageRequest"
            :show-file-list="false"
            accept="image/*"
            :before-upload="beforeImageUpload"
          >
            <el-tooltip content="发送图片" placement="top">
              <el-button size="small" icon="el-icon-picture-outline" circle></el-button>
            </el-tooltip>
          </el-upload>
        </div>
        <el-input
          v-model="inputText"
          type="textarea"
          :rows="4"
          placeholder="输入消息，Enter 发送，Shift+Enter 换行"
          resize="none"
          @keydown.enter.native="onEnterKey"
          class="chat-textarea"
        ></el-input>
        <div class="chat-send-bar">
          <el-button type="primary" size="small" @click="sendTextMessage" :loading="sending" :disabled="!inputText.trim()">发送</el-button>
        </div>
      </div>
    </div>

    <!-- 未选中会话时的占位 -->
    <div class="chat-empty" v-else>
      <div class="chat-empty-content">
        <i class="el-icon-chat-dot-round chat-empty-icon"></i>
        <p>请选择一个会话开始聊天</p>
      </div>
    </div>
  </div>
</template>

<script>
import { axios } from '../../config/axios';
import { WS_BASE_URL } from '../../config/config';

const ADMIN_USER_ID = 0;

export default {
  name: 'customerService-stat',
  data() {
    return {
      conversations: [],
      convLoading: false,
      convLoadingMore: false,
      convPage: 1,
      convPageSize: 20,
      convTotal: 0,

      currentConv: null,
      messages: [],
      msgLoading: false,
      msgLoadingMore: false,
      msgPage: 0,
      msgPageSize: 20,
      noMoreMessages: false,

      inputText: '',
      sending: false,

      ws: null,
      wsConnected: false,
      wsReconnectCount: 0,
      wsMaxReconnect: 5,
      wsHeartbeatTimer: null,
      wsManualClose: false,
    };
  },

  computed: {
    totalUnread() {
      return this.conversations.reduce((sum, c) => sum + (parseInt(c.unreadCount) || 0), 0);
    }
  },

  created() {
    this.loadConversations();
    this.connectWebSocket();
  },

  beforeDestroy() {
    this.wsManualClose = true;
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close(1000, '页面销毁');
    }
  },

  methods: {
    // ==================== 会话列表 ====================
    loadConversations(append = false) {
      if (append) {
        this.convLoadingMore = true;
      } else {
        this.convLoading = true;
        this.convPage = 1;
      }
      axios.get('conversation/conversations', {
        params: { pageNumber: this.convPage, pageSize: this.convPageSize }
      }).then(res => {
        if (res.code === 200) {
          const list = res.data.list || [];
          if (append) {
            this.conversations = this.conversations.concat(list);
          } else {
            this.conversations = list;
          }
          this.convTotal = res.data.total || 0;
        }
      }).finally(() => {
        this.convLoading = false;
        this.convLoadingMore = false;
      });
    },

    loadMoreConversations() {
      this.convPage++;
      this.loadConversations(true);
    },

    selectConversation(conv) {
      if (this.currentConv && this.currentConv.id === conv.id) return;
      this.currentConv = conv;
      this.messages = [];
      this.msgPage = 0;
      this.noMoreMessages = false;
      this.loadMessages(false);
      if (parseInt(conv.unreadCount) > 0) {
        this.clearUnread(conv);
      }
      // 切换会话时确保 WebSocket 保持连接
      this.ensureWebSocketConnected();
    },

    ensureWebSocketConnected() {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) return;
      // 重置手动关闭标志及重连计数，发起重连
      this.wsManualClose = false;
      this.wsReconnectCount = 0;
      this.stopHeartbeat();
      if (this.ws) {
        this.ws.onclose = null;
        this.ws.onerror = null;
        this.ws.close();
        this.ws = null;
      }
      this.connectWebSocket();
    },

    updateConvUnread(conversationId, unreadCount) {
      const conv = this.conversations.find(c => c.conversationId === conversationId);
      if (conv) {
        conv.unreadCount = unreadCount;
      }
    },

    updateConvLastMessage(conversationId, content, time) {
      const idx = this.conversations.findIndex(c => c.conversationId === conversationId);
      if (idx !== -1) {
        this.conversations[idx].lastMessageContent = content;
        this.conversations[idx].lastMessageTime = time;
        // 置顶到第一位
        const conv = this.conversations.splice(idx, 1)[0];
        this.conversations.unshift(conv);
      }
    },

    // ==================== 消息 ====================
    loadMessages(prepend = false) {
      if (prepend) {
        this.msgLoadingMore = true;
      } else {
        this.msgLoading = true;
      }
      const userId = this.currentConv.userIdOne;
      axios.get('conversation/conversations/messages', {
        params: {
          userId,
          productId: 0,
          refresh: prepend ? 0 : 1,
          pageNumber: this.msgPage,
          pageSize: this.msgPageSize
        }
      }).then(res => {
        if (res.code === 200) {
          // API 返回最新消息在前，反转后使最旧消息在前、最新在后（底部）
          const list = (res.data.list || []).map(m => ({
            ...m,
            fromUserId: String(m.fromUserId),
          })).reverse();
          if (prepend) {
            if (list.length < this.msgPageSize) {
              this.noMoreMessages = true;
            }
            const container = this.$refs.messagesContainer;
            const prevScrollHeight = container ? container.scrollHeight : 0;
            const existingIds = new Set(this.messages.map(m => m.messageId || m.id));
            const newList = list.filter(m => !existingIds.has(m.messageId || m.id));
            this.messages = newList.concat(this.messages);
            this.$nextTick(() => {
              if (container) {
                container.scrollTop = container.scrollHeight - prevScrollHeight;
              }
            });
          } else {
            this.messages = list;
            if (list.length < this.msgPageSize) {
              this.noMoreMessages = true;
            }
            this.$nextTick(() => this.scrollToBottom());
          }
        }
      }).finally(() => {
        this.msgLoading = false;
        this.msgLoadingMore = false;
      });
    },

    onMessageScroll() {
      const container = this.$refs.messagesContainer;
      if (!container) return;
      if (container.scrollTop === 0 && !this.msgLoadingMore && !this.noMoreMessages) {
        this.msgPage++;
        this.loadMessages(true);
      }
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    // ==================== 发送消息 ====================
    sendTextMessage() {
      const text = this.inputText.trim();
      if (!text || !this.currentConv) return;
      this.sending = true;
      const msgId = 'MSG_' + Date.now() + '_0';
      const payload = {
        messageId: msgId,
        conversationId: this.currentConv.conversationId,
        fromUserId: ADMIN_USER_ID,
        toUserId: this.currentConv.userIdOne,
        messageType: 1,
        content: text,
      };
      axios.post('conversation/customerServiceSend', payload, {
        headers: { 'Content-Type': 'application/json' },
        transformRequest: [(data) => JSON.stringify(data)],
      }).then(res => {
        if (res.code === 200) {
          this.inputText = '';
          const newMsg = {
            messageId: msgId,
            conversationId: this.currentConv.conversationId,
            fromUserId: '0',
            toUserId: String(this.currentConv.userIdOne),
            messageType: 1,
            content: text,
            sendTime: this.formatNow(),
            status: 1,
          };
          this.messages.push(newMsg);
          this.updateConvLastMessage(this.currentConv.conversationId, text, newMsg.sendTime);
          this.$nextTick(() => this.scrollToBottom());
        } else {
          this.$message.error('发送失败：' + res.message);
        }
      }).catch(() => {
        this.$message.error('发送失败，请重试');
      }).finally(() => {
        this.sending = false;
      });
    },

    onEnterKey(e) {
      if (e.shiftKey) return;
      e.preventDefault();
      this.sendTextMessage();
    },

    // ==================== 图片上传 ====================
    beforeImageUpload(file) {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        this.$message.error('只能上传图片文件');
        return false;
      }
      if (file.size > 150 * 1024 * 1024) {
        this.$message.error('图片大小不能超过 150MB');
        return false;
      }
      return true;
    },

    uploadImageRequest({ file }) {
      if (!this.currentConv) return;
      const formData = new FormData();
      formData.append('file', file);
      axios.post('conversation/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        transformRequest: [(data) => data],
      }).then(res => {
        if (res.code === 200) {
          const imageUrl = typeof res.data === 'string' ? res.data : (res.data && (res.data.url || res.data.imageUrl || res.data.filePath || ''));
          const msgId = 'MSG_' + Date.now() + '_0';
          const payload = {
            messageId: msgId,
            conversationId: this.currentConv.conversationId,
            fromUserId: ADMIN_USER_ID,
            toUserId: this.currentConv.userIdOne,
            messageType: 2,
            content: imageUrl,
          };
          axios.post('conversation/customerServiceSend', payload, {
            headers: { 'Content-Type': 'application/json' },
            transformRequest: [(data) => JSON.stringify(data)],
          }).then(sendRes => {
            if (sendRes.code === 200) {
              const newMsg = {
                messageId: msgId,
                conversationId: this.currentConv.conversationId,
                fromUserId: '0',
                messageType: 2,
                content: imageUrl,
                sendTime: this.formatNow(),
                status: 1,
              };
              this.messages.push(newMsg);
              this.updateConvLastMessage(this.currentConv.conversationId, '[图片]', newMsg.sendTime);
              this.$nextTick(() => this.scrollToBottom());
            } else {
              this.$message.error('发送图片失败');
            }
          });
        } else {
          this.$message.error('上传图片失败：' + res.message);
        }
      });
    },

    // ==================== 清除未读 ====================
    clearUnread(conv) {
      axios.post('conversation/conversations/clearUnread', {
        conversationId: conv.conversationId,
        toUserId: conv.userIdOne,
      }, {
        headers: { 'Content-Type': 'application/json' },
        transformRequest: [(data) => JSON.stringify(data)],
      }).then(res => {
        if (res.code === 200) {
          conv.unreadCount = 0;
        }
      });
    },

    // ==================== WebSocket ====================
    connectWebSocket() {
      const url = `${WS_BASE_URL}/${ADMIN_USER_ID}`;
      try {
        this.ws = new WebSocket(url, ['websocket']);
      } catch (e) {
        console.error('WebSocket 连接失败:', e);
        return;
      }

      this.ws.onopen = () => {
        this.wsConnected = true;
        this.wsReconnectCount = 0;
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleWsMessage(data);
        } catch (e) {
          console.error('WebSocket 消息解析失败:', e);
        }
      };

      this.ws.onerror = () => {
        this.wsConnected = false;
      };

      this.ws.onclose = (e) => {
        this.wsConnected = false;
        this.stopHeartbeat();
        if (!this.wsManualClose && e.code !== 1000 && this.wsReconnectCount < this.wsMaxReconnect) {
          setTimeout(() => {
            this.wsReconnectCount++;
            this.connectWebSocket();
          }, 3000);
        }
      };
    },

    handleWsMessage(data) {
      switch (data.type) {
        case 'connect':
          break;
        case 'heartbeat':
          break;
        case 'newMessage': {
          const msg = data.message;
          if (!msg) break;
          // 如果当前打开的对话匹配，追加消息
          if (this.currentConv && msg.conversationId === this.currentConv.conversationId) {
            const formatted = { ...msg, fromUserId: String(msg.fromUserId) };
            this.messages.push(formatted);
            this.$nextTick(() => this.scrollToBottom());
            // 当前对话已打开，自动清除未读
            this.clearUnread(this.currentConv);
          } else {
            // 其他会话未读数 +1
            const conv = this.conversations.find(c => c.conversationId === msg.conversationId);
            if (conv) {
              conv.unreadCount = (parseInt(conv.unreadCount) || 0) + 1;
            }
          }
          // 更新会话最后一条消息
          if (msg.conversationId) {
            this.updateConvLastMessage(msg.conversationId, msg.content, msg.sendTime);
          }
          break;
        }
        case 'conversationRefresh':
          // 刷新会话列表
          this.loadConversations();
          break;
        case 'readReceipt':
          break;
        default:
          break;
      }
    },

    startHeartbeat() {
      this.wsHeartbeatTimer = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ type: 'heartbeat', timestamp: Date.now() }));
        }
      }, 30000);
    },

    stopHeartbeat() {
      if (this.wsHeartbeatTimer) {
        clearInterval(this.wsHeartbeatTimer);
        this.wsHeartbeatTimer = null;
      }
    },

    // ==================== 工具方法 ====================
    formatTime(timeStr) {
      if (!timeStr) return '';
      const date = new Date(timeStr.replace(/-/g, '/'));
      const now = new Date();
      const diff = now - date;
      if (diff < 60 * 1000) return '刚刚';
      if (diff < 60 * 60 * 1000) return Math.floor(diff / 60000) + '分钟前';
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (date >= today) {
        return date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0');
      }
      const yesterday = new Date(today - 86400000);
      if (date >= yesterday) return '昨天';
      return (date.getMonth() + 1) + '/' + date.getDate();
    },

    formatMsgTime(timeStr) {
      if (!timeStr) return '';
      const date = new Date(timeStr.replace ? timeStr.replace(/-/g, '/') : timeStr);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (date >= today) {
        return date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0');
      }
      const yesterday = new Date(today - 86400000);
      if (date >= yesterday) {
        return '昨天 ' + date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0');
      }
      return (date.getMonth() + 1) + '月' + date.getDate() + '日 ' +
        date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0');
    },

    shouldShowTime(index) {
      if (index === 0) return true;
      const cur = this.messages[index];
      const prev = this.messages[index - 1];
      const curTime = new Date((cur.sendTime || cur.createdAt || '').replace(/-/g, '/'));
      const prevTime = new Date((prev.sendTime || prev.createdAt || '').replace(/-/g, '/'));
      return (curTime - prevTime) > 5 * 60 * 1000;
    },

    formatNow() {
      const d = new Date();
      const pad = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
  }
};
</script>

<style scoped>
.customer-service {
  display: flex;
  height: calc(100vh - 80px);
  background: #f5f6fa;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

/* 左侧会话列表 */
.conv-list {
  width: 280px;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #e8ecf0;
}
.conv-list-header {
  display: flex;
  align-items: center;
  padding: 16px 16px 12px;
  border-bottom: 1px solid #f0f2f5;
}
.conv-list-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
}
.total-unread-badge {
  margin-left: 8px;
}
.conv-list-body {
  flex: 1;
  overflow-y: auto;
}
.conv-list-body::-webkit-scrollbar {
  width: 4px;
}
.conv-list-body::-webkit-scrollbar-thumb {
  background: #dde1e7;
  border-radius: 2px;
}
.conv-item {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #f5f6fa;
}
.conv-item:hover {
  background: #f5f7ff;
}
.conv-item.active {
  background: #ecf0ff;
}
.conv-avatar-badge {
  flex-shrink: 0;
  margin-right: 10px;
}
.conv-info {
  flex: 1;
  min-width: 0;
}
.conv-info-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.conv-nickname {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 110px;
}
.conv-time {
  font-size: 11px;
  color: #909399;
  flex-shrink: 0;
  margin-left: 4px;
}
.conv-last-msg {
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.conv-empty {
  text-align: center;
  color: #c0c4cc;
  padding: 40px 0;
  font-size: 14px;
}
.conv-load-more {
  text-align: center;
  padding: 8px 0;
  border-top: 1px solid #f0f2f5;
}

/* 右侧聊天区域 */
.chat-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #fff;
}
.chat-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafbfc;
}
.chat-empty-content {
  text-align: center;
  color: #c0c4cc;
}
.chat-empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}
.chat-empty-content p {
  font-size: 14px;
}
.chat-header {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid #e8ecf0;
  background: #fff;
  flex-shrink: 0;
}
.chat-header-name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin: 0 10px;
}
.chat-header-id {
  margin-right: auto;
}
.chat-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ws-status-tag {
  font-size: 11px;
}

/* 消息区域 */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  background: #f5f6fa;
}
.chat-messages::-webkit-scrollbar {
  width: 4px;
}
.chat-messages::-webkit-scrollbar-thumb {
  background: #dde1e7;
  border-radius: 2px;
}
.msg-loading-more {
  text-align: center;
  color: #909399;
  font-size: 12px;
  padding: 8px 0;
}
.no-more-msg {
  text-align: center;
  color: #c0c4cc;
  font-size: 12px;
  padding: 8px 0;
}
.no-msg {
  text-align: center;
  color: #c0c4cc;
  font-size: 14px;
  margin-top: 60px;
}
.msg-time-divider {
  text-align: center;
  font-size: 11px;
  color: #b0b8c4;
  margin: 12px 0 8px;
}
.msg-wrapper {
  margin-bottom: 16px;
}
.msg-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.msg-left .msg-row {
  justify-content: flex-start;
}
.msg-right .msg-row {
  justify-content: flex-end;
}
.msg-avatar {
  flex-shrink: 0;
  margin-bottom: 2px;
}
.admin-avatar {
  background: #409eff;
}
.msg-bubble {
  max-width: 60%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;
}
.bubble-left {
  background: #fff;
  color: #303133;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.bubble-right {
  background: #409eff;
  color: #fff;
  border-bottom-right-radius: 4px;
}
.msg-img {
  max-width: 200px;
  max-height: 200px;
  border-radius: 6px;
  display: block;
  cursor: pointer;
}
.sys-msg {
  color: #909399;
  font-size: 12px;
  font-style: italic;
}
.msg-status-bar {
  display: flex;
  justify-content: flex-end;
  padding-right: 44px;
  margin-top: 3px;
}
.msg-status {
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  gap: 1px;
}
.msg-status-read {
  color: #409eff;
}
.msg-status-delivered {
  color: #67c23a;
}
.msg-status-sent {
  color: #909399;
}
.msg-status-check2 {
  margin-left: -6px;
}

/* 输入区域 */
.chat-input-area {
  flex-shrink: 0;
  border-top: 1px solid #e8ecf0;
  background: #fff;
}
.chat-toolbar {
  padding: 8px 16px 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  border-bottom: 1px solid #f0f2f5;
}
.chat-send-bar {
  display: flex;
  justify-content: flex-end;
  padding: 6px 16px 10px;
}
.chat-textarea /deep/ .el-textarea__inner {
  border: none;
  border-radius: 0;
  padding: 12px 16px;
  font-size: 14px;
  resize: none;
  color: #303133;
  box-shadow: none;
}
.chat-textarea /deep/ .el-textarea__inner:focus {
  box-shadow: none;
}
</style>
